import { AuthContext, camelize } from '@repo-level/core-front';
import { tournaments } from 'api/cms/tournaments';
import type { TopPlayer } from 'api/tournaments';
import { userTournamentStatus } from 'api/tournaments';
import type { WSResponse } from 'contexts/realTimeService';
import type { FC } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { State } from './reducer';
import { ACTION_TYPE, reducer } from './reducer';

export const TournamentsContext = createContext<State>(null as any);

const initialState: State = {
  userTournamentsStatuses: [],
  cmsTournaments: [],
  pendingCMSTournaments: false,
};

export const TournamentsContextProvider: FC = ({ children }) => {
  const [tournamentsState, dispatchTournaments] = useReducer(
    reducer,
    initialState
  );

  const {
    i18n: { language },
  } = useTranslation();

  const { user, playerSettings } = useContext(AuthContext);

  const fetchUserTournamentStatus = useCallback(async () => {
    try {
      if (!user) return;

      const { data: userTournamentsStatuses } = await userTournamentStatus();

      dispatchTournaments({
        type: ACTION_TYPE.SET_USER_TOURNAMENTS_STATUSES,
        payload: { userTournamentsStatuses },
      });
    } catch {}
  }, [user]);

  const fetchCMSTournaments = useCallback(async () => {
    try {
      dispatchTournaments({
        type: ACTION_TYPE.SET_PENDING_CMS_TOURNAMENTS_STATUS,
        payload: { pending: true },
      });

      const { data } = await tournaments(language);

      dispatchTournaments({
        type: ACTION_TYPE.SET_CMS_TOURNAMENTS,
        payload: { tournaments: data },
      });

      dispatchTournaments({
        type: ACTION_TYPE.SET_PENDING_CMS_TOURNAMENTS_STATUS,
        payload: { pending: false },
      });
    } catch {}
  }, [language]);

  useEffect(() => {
    fetchUserTournamentStatus();
  }, [fetchUserTournamentStatus]);

  useEffect(() => {
    fetchCMSTournaments();
  }, [fetchCMSTournaments]);

  useEffect(() => {
    if (!playerSettings?.cent.user) return;

    // @ts-ignore
    const centrifuge = new Centrifuge({
      url: playerSettings.cent.url + '/connection/websocket',
      user: playerSettings.cent.user,
      timestamp: playerSettings.cent.timestamp,
      token: playerSettings.cent.token,
    });

    centrifuge.subscribe(
      `tournaments_statuses#${playerSettings.cent.user}`,
      (context: WSResponse<TopPlayer[]>) => {
        dispatchTournaments({
          type: ACTION_TYPE.UPDATE_USER_TOURNAMENT_STATUS,
          payload: {
            userTournamentStatus: camelize(context.data) as TopPlayer,
          },
        });
      }
    );

    centrifuge.connect();

    return () => {
      centrifuge.disconnect();
    };
  }, [playerSettings]);

  return (
    <TournamentsContext.Provider value={{ ...tournamentsState }}>
      {children}
    </TournamentsContext.Provider>
  );
};
